import React, { useState, useMemo } from 'react';
import { Calculator, Table2, Users, UtensilsCrossed, GlassWater, Wine, Check, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

const TablePlannerSection = ({ 
  guestCount = 0,
  rentals = [],
  selectedRentals = [],
  onApplyCalculations,
  isExpanded,
  onToggle
}) => {
  const [tableType, setTableType] = useState('round-60');
  const [includeWineGlasses, setIncludeWineGlasses] = useState(false);
  const [includeChargers, setIncludeChargers] = useState(false);
  const [napkinColor, setNapkinColor] = useState('white');
  const [tableclothColor, setTableclothColor] = useState('white');

  // Table configurations with seating capacity
  const tableConfigs = {
    'round-48': { name: '48" Round Table', seats: 6, tableclothSize: '90', id: 'tbl-round-48' },
    'round-60': { name: '60" Round Table', seats: 8, tableclothSize: '108', id: 'tbl-round-60' },
    'round-72': { name: '72" Round Table', seats: 10, tableclothSize: '120', id: 'tbl-round-72' },
    'banquet-6ft': { name: '6ft Banquet Table', seats: 8, tableclothSize: 'rect-6ft', id: 'tbl-banquet-6ft' },
    'banquet-8ft': { name: '8ft Banquet Table', seats: 10, tableclothSize: 'rect-8ft', id: 'tbl-banquet-8ft' }
  };

  // Item mappings to rental IDs
  const itemMappings = {
    napkins: {
      white: 'lin-napkin-white',
      ivory: 'lin-napkin-ivory',
      black: 'lin-napkin-black',
      navy: 'lin-napkin-navy',
      burgundy: 'lin-napkin-burgundy',
      sage: 'lin-napkin-sage',
      blush: 'lin-napkin-blush',
      gold: 'lin-napkin-gold'
    },
    tablecloths: {
      '90-white': 'lin-tc-90-white',
      '90-ivory': 'lin-tc-90-ivory',
      '108-white': 'lin-tc-108-white',
      '108-ivory': 'lin-tc-108-ivory',
      '120-white': 'lin-tc-120-white',
      '120-ivory': 'lin-tc-120-ivory',
      'rect-6ft-white': 'lin-tc-rect-6ft-white',
      'rect-6ft-ivory': 'lin-tc-rect-6ft-ivory',
      'rect-8ft-white': 'lin-tc-rect-8ft-white',
      'rect-8ft-ivory': 'lin-tc-rect-8ft-ivory'
    },
    plates: {
      dinner: 'china-dinner-10',
      salad: 'china-salad-8',
      bread: 'china-bread-6'
    },
    flatware: 'flat-gold-set', // 5pc set per person
    waterGlass: 'glass-water',
    wineGlass: 'glass-wine-red',
    charger: 'china-charger-gold'
  };

  // Calculate quantities based on guest count
  const calculations = useMemo(() => {
    if (!guestCount || guestCount <= 0) return null;

    const config = tableConfigs[tableType];
    const tablesNeeded = Math.ceil(guestCount / config.seats);
    const extraSettings = 5; // Extra 5% for backup

    return {
      tables: {
        quantity: tablesNeeded,
        id: config.id,
        name: config.name,
        perTable: config.seats
      },
      tablecloths: {
        quantity: tablesNeeded,
        id: itemMappings.tablecloths[`${config.tableclothSize}-${tableclothColor}`],
        name: `Tablecloth - ${config.tableclothSize}" ${tableclothColor}`
      },
      napkins: {
        quantity: guestCount + extraSettings,
        id: itemMappings.napkins[napkinColor],
        name: `Cloth Napkin - ${napkinColor}`
      },
      dinnerPlates: {
        quantity: guestCount + extraSettings,
        id: itemMappings.plates.dinner,
        name: 'Dinner Plate - 10" White'
      },
      saladPlates: {
        quantity: guestCount + extraSettings,
        id: itemMappings.plates.salad,
        name: 'Salad Plate - 8" White'
      },
      flatwareSets: {
        quantity: guestCount + extraSettings,
        id: itemMappings.flatware,
        name: 'Gold Flatware Set (5pc)'
      },
      waterGlasses: {
        quantity: guestCount + extraSettings,
        id: itemMappings.waterGlass,
        name: 'Water Goblet'
      },
      wineGlasses: includeWineGlasses ? {
        quantity: guestCount + extraSettings,
        id: itemMappings.wineGlass,
        name: 'Red Wine Glass'
      } : null,
      chargers: includeChargers ? {
        quantity: guestCount,
        id: itemMappings.charger,
        name: 'Charger Plate - Gold'
      } : null
    };
  }, [guestCount, tableType, napkinColor, tableclothColor, includeWineGlasses, includeChargers]);

  // Calculate total cost
  const totalCost = useMemo(() => {
    if (!calculations) return 0;
    
    let total = 0;
    Object.values(calculations).forEach(item => {
      if (item && item.id) {
        const rental = rentals.find(r => r.id === item.id);
        if (rental) {
          const price = rental.price_per_unit || rental.price || 0;
          total += price * item.quantity;
        }
      }
    });
    return total;
  }, [calculations, rentals]);

  // Apply calculations to selected rentals
  const handleApply = () => {
    if (!calculations) return;

    const itemsToAdd = [];
    Object.entries(calculations).forEach(([key, item]) => {
      if (item && item.id) {
        const rental = rentals.find(r => r.id === item.id);
        if (rental) {
          itemsToAdd.push({
            ...rental,
            quantity: item.quantity
          });
        }
      }
    });

    onApplyCalculations(itemsToAdd);
  };

  const napkinColors = ['white', 'ivory', 'black', 'navy', 'burgundy', 'sage', 'blush', 'gold'];
  const tableclothColors = ['white', 'ivory'];

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
        data-testid="table-planner-toggle"
      >
        <span className="flex items-center gap-2">
          <Calculator size={18} />
          Table Planner (Auto-Calculate)
          {guestCount > 0 && (
            <span className="text-sm font-normal text-gray-500">
              ({guestCount} guests)
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg space-y-4">
          <p className="text-sm text-gray-600">
            Automatically calculate tables, linens, plates, and place settings based on guest count
          </p>

          {guestCount <= 0 ? (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-center">
              <p className="text-amber-800 text-sm">
                ⚠️ Enter a guest count above to use the table planner
              </p>
            </div>
          ) : (
            <>
              {/* Configuration Options */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Table Type</Label>
                  <Select value={tableType} onValueChange={setTableType}>
                    <SelectTrigger className="h-9 text-sm bg-white" data-testid="table-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(tableConfigs).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.name} (seats {config.seats})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tablecloth Color</Label>
                  <Select value={tableclothColor} onValueChange={setTableclothColor}>
                    <SelectTrigger className="h-9 text-sm bg-white capitalize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tableclothColors.map(color => (
                        <SelectItem key={color} value={color} className="capitalize">{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Napkin Color</Label>
                  <Select value={napkinColor} onValueChange={setNapkinColor}>
                    <SelectTrigger className="h-9 text-sm bg-white capitalize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {napkinColors.map(color => (
                        <SelectItem key={color} value={color} className="capitalize">{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Wine Glasses</Label>
                    <Switch 
                      checked={includeWineGlasses} 
                      onCheckedChange={setIncludeWineGlasses}
                      data-testid="wine-glasses-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Charger Plates</Label>
                    <Switch 
                      checked={includeChargers} 
                      onCheckedChange={setIncludeChargers}
                      data-testid="chargers-toggle"
                    />
                  </div>
                </div>
              </div>

              {/* Calculations Display */}
              {calculations && (
                <div className="bg-white rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                    <Users size={16} />
                    Calculated for {guestCount} Guests
                  </h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                    {/* Tables */}
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                      <Table2 size={16} className="text-blue-600" />
                      <div>
                        <p className="font-semibold">{calculations.tables.quantity} Tables</p>
                        <p className="text-xs text-gray-500">{tableConfigs[tableType].name}</p>
                      </div>
                    </div>
                    
                    {/* Tablecloths */}
                    <div className="flex items-center gap-2 p-2 bg-pink-50 rounded">
                      <span className="text-pink-600">🧵</span>
                      <div>
                        <p className="font-semibold">{calculations.tablecloths.quantity} Tablecloths</p>
                        <p className="text-xs text-gray-500 capitalize">{tableclothColor}</p>
                      </div>
                    </div>
                    
                    {/* Napkins */}
                    <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                      <span className="text-purple-600">🧻</span>
                      <div>
                        <p className="font-semibold">{calculations.napkins.quantity} Napkins</p>
                        <p className="text-xs text-gray-500 capitalize">{napkinColor}</p>
                      </div>
                    </div>
                    
                    {/* Dinner Plates */}
                    <div className="flex items-center gap-2 p-2 bg-amber-50 rounded">
                      <UtensilsCrossed size={16} className="text-amber-600" />
                      <div>
                        <p className="font-semibold">{calculations.dinnerPlates.quantity} Dinner Plates</p>
                        <p className="text-xs text-gray-500">10" white</p>
                      </div>
                    </div>
                    
                    {/* Salad Plates */}
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                      <UtensilsCrossed size={16} className="text-green-600" />
                      <div>
                        <p className="font-semibold">{calculations.saladPlates.quantity} Salad Plates</p>
                        <p className="text-xs text-gray-500">8" white</p>
                      </div>
                    </div>
                    
                    {/* Flatware Sets */}
                    <div className="flex items-center gap-2 p-2 bg-gray-100 rounded">
                      <span className="text-gray-600">🍴</span>
                      <div>
                        <p className="font-semibold">{calculations.flatwareSets.quantity} Flatware Sets</p>
                        <p className="text-xs text-gray-500">5pc gold</p>
                      </div>
                    </div>
                    
                    {/* Water Glasses */}
                    <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded">
                      <GlassWater size={16} className="text-cyan-600" />
                      <div>
                        <p className="font-semibold">{calculations.waterGlasses.quantity} Water Glasses</p>
                        <p className="text-xs text-gray-500">Goblet</p>
                      </div>
                    </div>
                    
                    {/* Wine Glasses (optional) */}
                    {calculations.wineGlasses && (
                      <div className="flex items-center gap-2 p-2 bg-red-50 rounded">
                        <Wine size={16} className="text-red-600" />
                        <div>
                          <p className="font-semibold">{calculations.wineGlasses.quantity} Wine Glasses</p>
                          <p className="text-xs text-gray-500">Red wine</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Chargers (optional) */}
                    {calculations.chargers && (
                      <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded">
                        <span className="text-yellow-600">✨</span>
                        <div>
                          <p className="font-semibold">{calculations.chargers.quantity} Chargers</p>
                          <p className="text-xs text-gray-500">Gold</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Total Cost */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <p className="text-sm text-gray-600">Estimated Rental Cost</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">${totalCost.toFixed(2)}</p>
                    </div>
                    <Button 
                      type="button"
                      onClick={handleApply}
                      className="bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white"
                      data-testid="apply-table-plan-btn"
                    >
                      <Check size={16} className="mr-2" />
                      Add All to Contract
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TablePlannerSection;
